import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Spin } from 'antd';
import { SendOutlined, CloseOutlined, RobotOutlined } from '@ant-design/icons';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Minimal inline formatter: render **bold** as actual bold, strip leading
// markdown bullet/heading markers, and preserve line breaks. Keeps us from
// pulling in a full markdown dependency for the little the model emits.
const renderRichText = (text: string): React.ReactNode =>
  text.split('\n').map((line, li) => {
    const clean = line.replace(/^\s*[-*]\s+/, '• ').replace(/^\s*#{1,6}\s+/, '');
    const parts = clean.split(/(\*\*[^*]+\*\*)/g);
    return (
      <React.Fragment key={li}>
        {li > 0 && <br />}
        {parts.map((seg, si) =>
          seg.startsWith('**') && seg.endsWith('**') ? (
            <strong key={si}>{seg.slice(2, -2)}</strong>
          ) : (
            <React.Fragment key={si}>{seg}</React.Fragment>
          ),
        )}
      </React.Fragment>
    );
  });

interface ReportAssistantProps {
  /** Fires the streaming request. Owner (modal) attaches auth + endpoint and
   *  returns the raw fetch Response so this component can read the SSE stream. */
  onSend: (messages: ChatMessage[]) => Promise<Response>;
  onClose: () => void;
  isMobile?: boolean;
}

/**
 * Chat panel for asking questions about the inspection report. Multi-turn,
 * streamed token-by-token. Self-contained — keeps its own message history and
 * reads the server-sent-events stream from `onSend`.
 */
export const ReportAssistant: React.FC<ReportAssistantProps> = ({ onSend, onClose, isMobile }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Keep the latest message in view as tokens stream in.
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const question = input.trim();
    if (!question || streaming) return;

    const next: ChatMessage[] = [...messages, { role: 'user', content: question }];
    // Add an empty assistant message we fill as the stream arrives.
    setMessages([...next, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);

    const setAssistant = (content: string) => {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content };
        return copy;
      });
    };

    try {
      const res = await onSend(next);
      if (!res.ok || !res.body) {
        setAssistant('Sorry — I could not get an answer. Please try again.');
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const obj = JSON.parse(line.slice(5).trim());
            if (obj.delta) {
              answer += obj.delta;
              setAssistant(answer);
            } else if (obj.error) {
              setAssistant(answer || 'Sorry — the assistant ran into an error.');
            }
          } catch {
            // ignore partial / non-JSON keepalive lines
          }
        }
      }
      if (!answer) setAssistant('Sorry — I could not get an answer. Please try again.');
    } catch (e) {
      setAssistant('Sorry — the request failed. Please check your connection and try again.');
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fff' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
          <RobotOutlined style={{ color: '#1677ff' }} />
          Ask about this report
        </span>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      {/* Messages */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {messages.length === 0 && (
          <div style={{ color: '#8c8c8c', fontSize: 13, lineHeight: 1.6 }}>
            Ask anything about this inspection report — e.g. "What are the most serious issues?",
            "Summarize the roof findings", or "Are there any safety concerns?"
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: 12,
                fontSize: 14,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: m.role === 'user' ? '#1677ff' : '#f2f3f5',
                color: m.role === 'user' ? '#fff' : '#1a1d23',
              }}
            >
              {m.content
                ? renderRichText(m.content)
                : streaming && i === messages.length - 1
                ? <Spin size="small" />
                : ''}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div style={{ borderTop: '1px solid #e8e8e8', padding: isMobile ? '8px 10px' : '10px 12px' }}>
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask a question about this report…"
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={streaming}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={send}
            loading={streaming}
            disabled={!input.trim()}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportAssistant;
